import { Link } from "react-router-dom";
import { absent } from "../lib/format";
import { useRecordNav } from "../ui/RecordPage";

/* A link from one record to another.
 *
 * The V1 Scope doc calls graph navigation "the thing no file can do", and it
 * is the one claim that separates this from the spreadsheet the team already
 * works from. On 2026-09-15 three of the four hops were dead ends: a company's
 * people, a person's employer and a property's owner all rendered as plain
 * text, so the only way out of a record was back to the list. Company to
 * property was the single hop that worked, and the pattern here is its.
 *
 * Three states, because they are three different facts:
 *
 *   no name          the engine holds nobody there, so a dash
 *   a name, no id    we know who it is and not which record, so plain text
 *   a name and an id a link
 *
 * The middle one is the reason this is a component rather than an inline
 * Link. A dead link that looks like a live one is worse than text, and the
 * views do return a name with no id: api.property carries owner_company_name
 * from a stakeholder edge that may point at a company merged away since.
 *
 * From inside a record page the link says where it came from, so the page
 * it opens can offer "Back to Acme Holdings" rather than "Back to companies",
 * and the page it leaves remembers its place for the way back. */
export function RecordLink({
  to, id, label, query,
}: {
  /** The list the record lives on. */
  to: "companies" | "people" | "properties";
  id?: string | null | undefined;
  label?: string | null | undefined;
  /** Extra view state for the page it opens, such as the campaign a
   *  property was opened from, without the leading "?". */
  query?: string | undefined;
}) {
  const nav = useRecordNav();
  if (!label) return <>{absent()}</>;
  if (!id) return <span title="No record for this name">{label}</span>;
  return (
    <Link
      to={`/${to}/${id}${query ? `?${query}` : ""}`}
      state={nav ? { from: nav.key, back: nav.title } : undefined}
      onClick={() => nav?.remember(null)}
    >
      {label}
    </Link>
  );
}
